/**
 * @fileoverview Callout component for highlighting information
 * @module ctl-callout
 * @version 1.0.0
 * @deprecated Use ctl-message with variant="callout" instead. This component is maintained for backwards compatibility.
 * 
 * @description
 * A callout component for highlighting important information with a thick left border.
 * 
 * **DEPRECATED**: This component has been replaced by the unified `ctl-message` component.
 * Please use `<ctl-message variant="callout">` instead. This standalone component is maintained for backwards compatibility.
 * 
 * @example
 * <!-- DEPRECATED: Old way -->
 * <ctl-callout tone="info">Did you know?</ctl-callout>
 * 
 * <!-- RECOMMENDED: New way -->
 * <ctl-message variant="callout" tone="info">Did you know?</ctl-message>
 */

import './ctl-message.js';
import { shadowQuery } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-callout-bg: var(--ctl-color-surface);
    --ctl-callout-text: var(--ctl-color-text);
    --ctl-callout-border: var(--ctl-color-border);
    --ctl-callout-radius: var(--ctl-radius-12);

    display: block;
    position: relative;
    background: var(--ctl-callout-bg);
    color: var(--ctl-callout-text);
    border: var(--ctl-border-width-thin) solid var(--ctl-callout-border);
    border-left-width: var(--ctl-space-1);
    border-radius: var(--ctl-callout-radius);
    padding: var(--ctl-space-4) var(--ctl-space-5);
    font: var(--ctl-font-weight-normal) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    line-height: var(--ctl-leading-relaxed);
  }

  /* Tone colors - neutral (default) */
  :host(:not([tone]), [tone="neutral"]) {
    --ctl-callout-border: var(--ctl-color-border);
  }

  /* Tone colors - info */
  :host([tone="info"]) {
    --ctl-callout-bg: color-mix(in srgb, var(--ctl-color-info) 5%, var(--ctl-color-surface));
    --ctl-callout-text: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    --ctl-callout-border: var(--ctl-color-info);
  }

  /* Tone colors - success */
  :host([tone="success"]) {
    --ctl-callout-bg: color-mix(in srgb, var(--ctl-color-success) 5%, var(--ctl-color-surface));
    --ctl-callout-text: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    --ctl-callout-border: var(--ctl-color-success);
  }

  /* Tone colors - warning */
  :host([tone="warning"]) {
    --ctl-callout-bg: color-mix(in srgb, var(--ctl-color-warning) 5%, var(--ctl-color-surface));
    --ctl-callout-text: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    --ctl-callout-border: var(--ctl-color-warning);
  }

  /* Tone colors - danger */
  :host([tone="danger"]) {
    --ctl-callout-bg: color-mix(in srgb, var(--ctl-color-danger) 5%, var(--ctl-color-surface));
    --ctl-callout-text: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    --ctl-callout-border: var(--ctl-color-danger);
  }

  .callout-container {
    display: flex;
    gap: var(--ctl-space-3-5);
    align-items: start;
  }

  .callout-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: var(--ctl-size-icon-lg);
    margin-top: var(--ctl-space-0-5);
  }

  .callout-icon:empty {
    display: none;
  }

  .callout-content {
    flex: 1;
    min-width: 0;
  }

  .callout-title {
    font: var(--ctl-font-weight-semibold) var(--ctl-font-size-lg) var(--ctl-font-sans, system-ui);
    margin: 0 0 var(--ctl-space-2);
  }

  .callout-title:empty {
    display: none;
  }

  .callout-body {
    display: block;
  }

  .callout-actions {
    display: flex;
    gap: var(--ctl-space-2-5);
    margin-top: var(--ctl-space-3-5);
  }

  .callout-actions:empty {
    display: none;
  }
</style>

<div class="callout-container" part="container">
  <div class="callout-icon" part="icon">
    <slot name="icon"></slot>
  </div>
  <div class="callout-content" part="content">
    <div class="callout-title" part="title">
      <slot name="title"></slot>
    </div>
    <div class="callout-body" part="body">
      <slot></slot>
    </div>
    <div class="callout-actions" part="actions">
      <slot name="actions"></slot>
    </div>
  </div>
</div>
`;

export class CtlCallout extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["tone", "title"];
  }

  connectedCallback() {
    // Set defaults
    if (!this.hasAttribute("tone")) this.setAttribute("tone", "neutral");

    // Set title element if title attribute exists
    this._updateTitle();
  }

  attributeChangedCallback(name) {
    if (name === "title") {
      this._updateTitle();
    }
  }

  _updateTitle() {
    const titleAttr = this.getAttribute("title");
    // If a slotted title is present, prefer that and do not manage textContent here.
    if (this.querySelector('[slot="title"]')) {
      return;
    }

    const titleElement = shadowQuery(this, ".callout-title");
    if (!titleElement) return;

    // When titleAttr is provided, set it; otherwise clear any previous text
    titleElement.textContent = titleAttr || "";
  }
}

customElements.define("ctl-callout", CtlCallout);
